
import * as React from 'react';
import { openItem } from '../../operations/openItem';
import { resolveItems, supportFacets } from '../../operations/getItems';
import { injectInitialProps } from '../../utilities/components/Props';
import { IItemCacheThunkAction, IItemCacheDispatch } from '../../store/actions/ItemCacheActions';
import { IAddon, createAddon } from '../../addons/registerOperationHandler';
import { IItemCacheProviderProps, ItemCacheProvider } from '../itemCacheProvider/ItemCacheProvider';
import { connectToItemsScope } from '../decorators/connectToItemsScope';
import { IRenderProps, renderChildrenWithProps } from '../../utilities/components/RenderProps';
import { mark, now } from '../../utilities/performance';
import { Omit } from '../../utilities/Omit';
import { readItems } from '../../operations/readItems';
import { IItemCache } from '../../models/IItemCacheObserver';
import { itemsScopeInitTimeFacet } from '../../facets/LoggerFacets';
import { IControlFaceted, renderChildrenWithControlHandlers } from '../decorators/withControlHandler';
import { itemsScopeControl } from '../../controls/itemsScopeControl';
import { itemsScopeItemKeyFacet, getNextItemsScopeItemKey } from '../../models/itemsScope/getNextItemsScopeItemKey';

class ItemsScopeContext extends React.PureComponent<IItemsScopeStateProps> {
  private _controlHandlers: IControlFaceted;

  constructor(props: IItemsScopeStateProps) {
    super(props);

    this._controlHandlers = itemsScopeControl.pack(this._itemsScopeControlHandler);
  }

  public render(): JSX.Element {
    return renderChildrenWithControlHandlers(this.props.children, this._controlHandlers);
  }

  private _itemsScopeControlHandler = (
    itemCache: IItemCache,
    props: typeof itemsScopeControl.inputType
  ): typeof itemsScopeControl.returnType => {
    if (props.itemsScopeItemKey) {
      // If a closer ItemsScope applies to a component, prefer it instead of overriding.
      return {};
    }

    return {
      itemsScopeItemKey: this.props.itemsScopeItemKey
    };
  }
}

const itemsScopeAddon = createAddon(() => {
  return (dispatch: IItemCacheDispatch) => {
    dispatch(supportFacets({
      keyFacet: itemsScopeItemKeyFacet,
      facets: []
    }));
  };
});

/**
 * @public
 */
export { getNextItemsScopeItemKey };

/**
 * @public
 */
export interface IItemsScopeInitializerPayload {
  itemsScopeItemKey: string;
}

/**
 * @public
 */
export type IItemsScopeInitializer = (payload: IItemsScopeInitializerPayload) => IItemCacheThunkAction<void>;

/**
 * Creates an initializer to be passed to `<ItemsScope initializer={ }>` for a given
 * starting data source and key.
 * @public
 */
export function createInitializer(addon: IAddon, itemKey: string): IItemsScopeInitializer {
  return (payload: {
    itemsScopeItemKey: string;
  }) => (dispatch: IItemCacheDispatch) => {
    dispatch(addon);
    dispatch(openItem({
      itemsScopeItemKey: payload.itemsScopeItemKey,
      currentItemKey: itemKey
    }));
  };
}

function logItemsScopeInitTime(itemsScopeItemKey: string): IItemCacheThunkAction<void> {
  return (dispatch: IItemCacheDispatch) => {
    mark(`ItemsScope.Init.Start (${itemsScopeItemKey})`);

    const initTime = dispatch(readItems((itemCache: IItemCache) => itemCache.demandItemFacet(itemsScopeInitTimeFacet, itemsScopeItemKey)));

    if (!initTime) {
      dispatch(resolveItems({
        items: {
          [itemsScopeItemKey]: {
            ...itemsScopeInitTimeFacet.pack(Math.round(now()))
          }
        }
      }));
    }
  };
}

/**
 * @public
 */
export interface IItemsScopeStateProps {
  itemsScopeItemKey: string;
}

/**
 * @public
 */
export type IItemsScopeProps = Omit<IItemCacheProviderProps, 'initializer'> & IRenderProps<IItemsScopeStateProps> & {
  /**
   * An initializer for the items scope to select the starting data source and key.
   */
  initializer?: IItemsScopeInitializer;
  itemsScopeItemKey?: string;
};

/**
 * Component which maintains a state with a current item and selection,
 * providing access to a default item cache and behaviors.
 * @public
 */
export const ItemsScope: React.ComponentType<IItemsScopeProps> =
  injectInitialProps((
    props: Pick<IItemsScopeProps, 'itemsScopeItemKey' | 'initializer'>
  ) => {
    const {
      // getNextItemsScopeItemKey() should only be called once for each new ItemsScope.
      itemsScopeItemKey = getNextItemsScopeItemKey(),
      initializer
    } = props;

    // Wrap the provided initializer with one that adds default instrumentation
    // and which registers the 'addon'.
    const itemCacheInitializer = () => {
      return (dispatch: IItemCacheDispatch) => {
        dispatch(itemsScopeAddon);

        dispatch(logItemsScopeInitTime(itemsScopeItemKey));

        if (initializer) {
          dispatch(initializer({
            itemsScopeItemKey
          }));
        }
      };
    };

    return {
      itemsScopeItemKey,
      initializer: itemCacheInitializer
    };
  })(ItemsScopeInner);

function ItemsScopeInner(
  props: IItemCacheProviderProps & IItemsScopeStateProps & IRenderProps<IItemsScopeStateProps>
): JSX.Element {
  const {
    itemsScopeItemKey,
    children,
    // Need to prevent the above props from propagating to the ItemCacheProvider.
    ...itemCacheProps
  } = props;

  return (
    <ItemCacheProvider { ...itemCacheProps }>
      <ItemsScopeContext itemsScopeItemKey={ itemsScopeItemKey }>
        {
          renderChildrenWithProps(children, {
            itemsScopeItemKey
          })
        }
      </ItemsScopeContext>
    </ItemCacheProvider>
  );
}

/**
 * @public
 */
export interface IWithItemsScopeProps extends IRenderProps<IItemsScopeStateProps> {
  // Nothing additional.
}

/**
 * Render-prop component which provides access to the itemsScopeItemKey for the current ItemsScope.
 * @public
 */
export const WithItemsScope: React.ComponentType<IWithItemsScopeProps> = connectToItemsScope()(WithItemsScopeCallback);

WithItemsScope.displayName = 'WithItemsScope';

function WithItemsScopeCallback(props: IItemsScopeStateProps & IWithItemsScopeProps): JSX.Element | null {
  const {
    itemsScopeItemKey,
    children
  } = props;

  return renderChildrenWithProps(children, {
    itemsScopeItemKey
  });
}
